import DOMPurify, { type Config } from 'dompurify';

/**
 * Angular's `[innerHTML]` binding runs the value through its built-in DomSanitizer (Angular 17.3, `@angular/core`
 * html_sanitizer) before it reaches the DOM. This reproduces that policy on top of DOMPurify, a vetted and actively
 * maintained sanitiser, instead of a hand-rolled parser:
 *
 * - elements: Angular's VALID_ELEMENTS allow-list only (no svg, math, form controls, iframe, object, ...); unknown
 *   elements lose their tag but keep their text; `script`, `style` and `template` are dropped with their content;
 * - attributes: Angular's VALID_ATTRS allow-list only (no `style`, `id`, `name`, `on*`, `data-*`);
 * - URLs: the attributes Angular treats as URLs (`href`, `src`, `background`, `cite`, `longdesc`, `poster`, `itemtype`,
 *   `xlink:href`) must match Angular's SAFE_URL_PATTERN, and additionally may not use `data:` (except `src` on
 *   img/audio/video/source/track, where DOMPurify permits it) or `vbscript:`.
 *
 * Deliberate differences from Angular (both stricter): an unsafe URL attribute is removed instead of being rewritten to
 * `unsafe:...`, and the content of svg/math/noscript and similar foreign or raw-text containers is dropped rather than
 * traversed (the usual mutation-XSS carriers). See PARITY_DECISIONS D29.
 */
const list = (value: string) => value.split(',');

const ALLOWED_TAGS = list(
  [
    // VOID_ELEMENTS
    'area,br,col,hr,img,wbr',
    // OPTIONAL_END_TAG_BLOCK_ELEMENTS + OPTIONAL_END_TAG_INLINE_ELEMENTS
    'colgroup,dd,dt,li,p,tbody,td,tfoot,th,thead,tr,rp,rt',
    // BLOCK_ELEMENTS
    'address,article,aside,blockquote,caption,center,del,details,dialog,dir,div,dl,figure,figcaption,footer,h1,h2,h3,h4,h5,h6,header,hgroup,hr,ins,main,map,menu,nav,ol,pre,section,summary,table,ul',
    // INLINE_ELEMENTS
    'a,abbr,acronym,audio,b,bdi,bdo,big,br,cite,code,del,dfn,em,font,i,img,ins,kbd,label,map,mark,picture,q,ruby,rp,rt,s,samp,small,source,span,strike,strong,sub,sup,time,track,tt,u,var,video',
  ].join(','),
);

const URI_ATTRS = list('background,cite,href,itemtype,longdesc,poster,src,xlink:href');

const HTML_ATTRS = list(
  'abbr,accesskey,align,alt,autoplay,axis,bgcolor,border,cellpadding,cellspacing,class,clear,color,cols,colspan,compact,controls,coords,datetime,default,dir,download,face,headers,height,hidden,hreflang,hspace,ismap,itemscope,itemprop,kind,label,lang,language,loop,media,muted,nohref,nowrap,open,preload,rel,rev,role,rows,rowspan,rules,scope,scrolling,shape,size,sizes,span,srclang,srcset,start,summary,tabindex,target,title,translate,type,usemap,valign,value,vspace,width',
);

const ARIA_ATTRS = list(
  'aria-activedescendant,aria-atomic,aria-autocomplete,aria-busy,aria-checked,aria-colcount,aria-colindex,aria-colspan,aria-controls,aria-current,aria-describedby,aria-details,aria-disabled,aria-dropeffect,aria-errormessage,aria-expanded,aria-flowto,aria-grabbed,aria-haspopup,aria-hidden,aria-invalid,aria-keyshortcuts,aria-label,aria-labelledby,aria-level,aria-live,aria-modal,aria-multiline,aria-multiselectable,aria-orientation,aria-owns,aria-placeholder,aria-posinset,aria-pressed,aria-readonly,aria-relevant,aria-required,aria-roledescription,aria-rowcount,aria-rowindex,aria-rowspan,aria-selected,aria-setsize,aria-sort,aria-valuemax,aria-valuemin,aria-valuenow,aria-valuetext',
);

/**
 * Angular's SAFE_URL_PATTERN with `data:` and `vbscript:` also refused. DOMPurify separately re-allows `data:` for `src`
 * on img/audio/video/source/track, which cannot execute script.
 */
const SAFE_URL = /^(?!javascript:|vbscript:|data:)(?:[a-z0-9+.-]+:|[^&:/?#]*(?:[/?#]|$))/i;

/** Elements whose content is dropped along with the tag (Angular: script, style, template; the rest are mXSS carriers). */
const FORBID_CONTENTS = list(
  'script,style,template,svg,math,annotation-xml,foreignobject,desc,title,head,noscript,noembed,noframes,plaintext,xmp',
);

const CONFIG: Config & { RETURN_DOM: false; RETURN_DOM_FRAGMENT: false; RETURN_TRUSTED_TYPE: false } = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: [...URI_ATTRS, ...HTML_ATTRS, ...ARIA_ATTRS],
  // Angular only URL-checks URI_ATTRS; DOMPurify would otherwise apply the URL pattern to every attribute value.
  ADD_URI_SAFE_ATTR: [...HTML_ATTRS.filter((name) => name !== 'srcset'), ...ARIA_ATTRS],
  ALLOWED_URI_REGEXP: SAFE_URL,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
  KEEP_CONTENT: true,
  FORBID_CONTENTS,
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  RETURN_TRUSTED_TYPE: false,
};

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), CONFIG);
}
